// Day-window fetchers for sleep / feed / pump / diaper, returning a unified
// event stream sorted by time. Used by Home, Chart, Stats.

import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AnyEvent,
  DiaperEvent,
  FeedingSession,
  PumpingSession,
  SleepSession,
} from '@shared/types.ts';
import type { DiaperKind, FeedType, PumpSide, SleepLocation } from '@shared/constants.ts';
import { api } from './client.ts';

export type WindowParams = { from: number; to: number; babyId?: string };

const q = ({ from, to, babyId }: WindowParams) => {
  const p = new URLSearchParams({ from: String(from), to: String(to) });
  if (babyId) p.set('baby', babyId);
  return p.toString();
};

export function useSleep(w: WindowParams) {
  return useQuery({
    queryKey: ['sleep', w],
    queryFn: () => api<{ sessions: SleepSession[] }>(`/api/sleep?${q(w)}`),
    select: (d) => d.sessions,
  });
}
export function useFeed(w: WindowParams) {
  return useQuery({
    queryKey: ['feed', w],
    queryFn: () => api<{ sessions: FeedingSession[] }>(`/api/feed?${q(w)}`),
    select: (d) => d.sessions,
  });
}
export function usePump(w: WindowParams) {
  return useQuery({
    queryKey: ['pump', w],
    queryFn: () => api<{ sessions: PumpingSession[] }>(`/api/pump?${q(w)}`),
    select: (d) => d.sessions,
  });
}
export function useDiaper(w: WindowParams) {
  return useQuery({
    queryKey: ['diaper', w],
    queryFn: () => api<{ events: DiaperEvent[] }>(`/api/diaper?${q(w)}`),
    select: (d) => d.events,
  });
}

export function useTodayEvents(babyId?: string): {
  sleep: SleepSession[];
  feed: FeedingSession[];
  pump: PumpingSession[];
  diaper: DiaperEvent[];
  inProgressSleep: SleepSession | null;
  inProgressBreast: FeedingSession | null;
  inProgressPump: PumpingSession | null;
  combined: AnyEvent[];
  isLoading: boolean;
} {
  // 36h window so we always pick up cross-midnight sleep. Bucket to the nearest
  // minute so the query key is stable across renders (otherwise every Date.now()
  // increment makes a new cache key and React Query treats it as a miss, which
  // causes the visible loading flash on every click/tick.)
  const w = useMemo(() => {
    const nowMin = Math.floor(Date.now() / 60_000) * 60;
    const to = nowMin + 3600;
    const from = to - 36 * 3600;
    return { from, to, babyId };
  }, [babyId, bucketMinute()]);
  const s = useSleep(w);
  const f = useFeed(w);
  const p = usePump(w);
  const d = useDiaper(w);
  const sleep = s.data ?? [];
  const feed = f.data ?? [];
  const pump = p.data ?? [];
  const diaper = d.data ?? [];

  const inProgressSleep = sleep.find((x) => x.ended_at == null) ?? null;
  const inProgressBreast =
    feed.find((x) => x.ended_at == null && (x.type === 'breast_left' || x.type === 'breast_right')) ?? null;
  const inProgressPump = pump.find((x) => x.ended_at == null) ?? null;

  const combined: AnyEvent[] = [
    ...sleep.map((x) => ({ _t: 'sleep' as const, ...x })),
    ...feed.map((x) => ({ _t: 'feed' as const, ...x })),
    ...pump.map((x) => ({ _t: 'pump' as const, ...x })),
    ...diaper.map((x) => ({ _t: 'diaper' as const, ...x })),
  ].sort((a, b) => {
    const ta = a._t === 'diaper' ? a.occurred_at : a.started_at;
    const tb = b._t === 'diaper' ? b.occurred_at : b.started_at;
    return tb - ta;
  });

  return {
    sleep,
    feed,
    pump,
    diaper,
    inProgressSleep,
    inProgressBreast,
    inProgressPump,
    combined,
    isLoading: s.isLoading || f.isLoading || p.isLoading || d.isLoading,
  };
}

// Recomputes once per minute via a quietly-updated hook below — needed so the
// useMemo deps array is a stable scalar instead of `Date.now()` directly.
function bucketMinute(): number {
  // No state: every render reads current minute; React's reconciler runs this
  // on each render of the consumer. We DON'T return a new array each call to
  // avoid breaking the dep-array length contract, so it stays a number.
  return Math.floor(Date.now() / 60_000);
}

export function useInvalidateEvents() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['sleep'] });
    qc.invalidateQueries({ queryKey: ['feed'] });
    qc.invalidateQueries({ queryKey: ['pump'] });
    qc.invalidateQueries({ queryKey: ['diaper'] });
    qc.invalidateQueries({ queryKey: ['stats'] });
  };
}

// ─── Mutations ──────────────────────────────────────────────

export function useStartSleep() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: (vars: { location?: SleepLocation }) =>
      api<{ session: SleepSession }>('/api/sleep/start', { method: 'POST', json: vars }),
    onSuccess: () => inv(),
  });
}
export function useStopSleep() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: () => api<{ session: SleepSession }>('/api/sleep/stop', { method: 'POST', json: {} }),
    onSuccess: () => inv(),
  });
}
export function useDeleteSleep() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: (id: string) => api(`/api/sleep/${id}`, { method: 'DELETE' }),
    onSuccess: () => inv(),
  });
}

// Re-open a stopped sleep session (undo of "End sleep"). Sets ended_at back to null.
export function useReopenSleep() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ session: SleepSession }>(`/api/sleep/${id}`, {
        method: 'PATCH',
        json: { ended_at: null },
      }),
    onSuccess: () => inv(),
  });
}

// Switch side mid-breast-feed. Server appends a segment + flips the type.
// Counts as one feed in stats.
export function useSwitchBreast() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ session: FeedingSession }>(`/api/feed/${id}/switch-side`, {
        method: 'POST',
        json: {},
      }),
    onSuccess: () => inv(),
  });
}

export function usePauseFeed() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ session: FeedingSession }>(`/api/feed/${id}/pause`, {
        method: 'POST',
        json: {},
      }),
    onSuccess: () => inv(),
  });
}

export function useResumeFeed() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ session: FeedingSession }>(`/api/feed/${id}/resume`, {
        method: 'POST',
        json: {},
      }),
    onSuccess: () => inv(),
  });
}

// Re-open a stopped feed (undo of "End feed"). Sets ended_at back to null.
export function useReopenFeed() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ session: FeedingSession }>(`/api/feed/${id}`, {
        method: 'PATCH',
        json: { ended_at: null },
      }),
    onSuccess: () => inv(),
  });
}

// PATCH a sleep session (location, notes, started_at, ended_at).
export function useUpdateSleep() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api<{ session: SleepSession }>(`/api/sleep/${id}`, { method: 'PATCH', json: patch }),
    onSuccess: () => inv(),
  });
}

// PATCH a feeding session (notes, type, amount_ml, content, started_at, ended_at).
export function useUpdateFeed() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api<{ session: FeedingSession }>(`/api/feed/${id}`, { method: 'PATCH', json: patch }),
    onSuccess: () => inv(),
  });
}

// PATCH a pumping session (side, yield_*, notes, started_at, ended_at).
export function useUpdatePump() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      api<{ session: PumpingSession }>(`/api/pump/${id}`, { method: 'PATCH', json: patch }),
    onSuccess: () => inv(),
  });
}

export function useStartFeed() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: (vars: { type: FeedType }) =>
      api<{ session: FeedingSession }>('/api/feed/start', { method: 'POST', json: vars }),
    onSuccess: () => inv(),
  });
}
export function useStopFeed() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: (vars: { type: FeedType }) =>
      api<{ session: FeedingSession }>('/api/feed/stop', { method: 'POST', json: vars }),
    onSuccess: () => inv(),
  });
}
export function useLogFeed() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: (vars: {
      type: FeedType;
      amount_ml?: number;
      started_at?: number;
      ended_at?: number;
      content?: 'formula' | 'expressed' | 'mix' | null;
      notes?: string | null;
    }) => {
      const at = vars.started_at ?? Math.floor(Date.now() / 1000);
      return api<{ session: FeedingSession }>('/api/feed', {
        method: 'POST',
        json: {
          type: vars.type,
          started_at: at,
          ended_at: vars.ended_at ?? at,
          amount_ml: vars.amount_ml ?? null,
          content: vars.content ?? null,
          notes: vars.notes ?? null,
        },
      });
    },
    onSuccess: () => inv(),
  });
}
export function useDeleteFeed() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: (id: string) => api(`/api/feed/${id}`, { method: 'DELETE' }),
    onSuccess: () => inv(),
  });
}

export function useStartPump() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: (vars: { side?: PumpSide }) =>
      api<{ session: PumpingSession }>('/api/pump/start', { method: 'POST', json: vars }),
    onSuccess: () => inv(),
  });
}
export function useStopPump() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: (vars: {
      id?: string;
      side?: PumpSide;
      yield_ml?: number;
      yield_left_ml?: number;
      yield_right_ml?: number;
    }) => api<{ session: PumpingSession }>('/api/pump/stop', { method: 'POST', json: vars }),
    onSuccess: () => inv(),
  });
}
export function useDeletePump() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: (id: string) => api(`/api/pump/${id}`, { method: 'DELETE' }),
    onSuccess: () => inv(),
  });
}

export function useLogDiaper() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: (vars: {
      kind: DiaperKind;
      consistency?: string | null;
      color?: string | null;
      leak?: boolean;
      notes?: string | null;
      occurred_at?: number;
    }) =>
      api<{ event: DiaperEvent }>('/api/diaper', {
        method: 'POST',
        json: { ...vars, occurred_at: vars.occurred_at ?? Math.floor(Date.now() / 1000) },
      }),
    onSuccess: () => inv(),
  });
}
export function useDeleteDiaper() {
  const inv = useInvalidateEvents();
  return useMutation({
    mutationFn: (id: string) => api(`/api/diaper/${id}`, { method: 'DELETE' }),
    onSuccess: () => inv(),
  });
}
